import React, { useEffect } from 'react';
import { F2120 } from '../../../services/pw2202Api';
import { fmt, fmtInt } from '../helpers';

interface Tab3Props {
  formData: Partial<F2120>;
  isEditing: boolean;
  getField: (key: keyof F2120) => F2120[keyof F2120];
  setField: (key: keyof F2120, value: F2120[keyof F2120]) => void;
  recalcMasterTotals: () => void;
}

/** Tab 3: 金額/備註 */
const Tab3AmountMemo: React.FC<Tab3Props> = React.memo(({
  formData, isEditing, getField, setField, recalcMasterTotals,
}) => {
  // Recalculate on tab mount to keep display in sync
  useEffect(() => {
    recalcMasterTotals();
  }, [recalcMasterTotals]);

  /** Handle transportation/freight change */
  const handleAmountChange = (key: 'f2120_23' | 'f2120_25', value: string) => {
    setField(key, parseFloat(value) || 0);
  };

  /** Handle tax type change [T-F03] */
  const handleTaxTypeChange = (val: number) => {
    setField('f2120_18', val);
  };

  // Compute displayed pending amount
  const grandTotal = (getField('f2120_20') as number) || 0;
  const paidAmount = (getField('f2120_21') as number) || 0;
  const pendingAmount = grandTotal - paidAmount;

  return (
    <div className="tab-content-section">
      <div className="form-grid">
        {/* 明細金額 (readonly) */}
        <label>明細金額</label>
        <div className="amount-display">{fmt(getField('f2120_17') as number)}</div>

        {/* 交通費 (F2120_23) */}
        <label>交通費</label>
        <div>
          <input
            type="number"
            value={getField('f2120_23') as number ?? ''}
            onChange={e => handleAmountChange('f2120_23', e.target.value)}
            disabled={!isEditing}
            style={{ width: 140, textAlign: 'right' }}
            step="0.01" data-contract-field="f2120_23"
          />
        </div>

        {/* 運費 (F2120_25) */}
        <label>運費</label>
        <div>
          <input
            type="number"
            value={getField('f2120_25') as number ?? ''}
            onChange={e => handleAmountChange('f2120_25', e.target.value)}
            disabled={!isEditing}
            style={{ width: 140, textAlign: 'right' }}
            step="0.01" data-contract-field="f2120_25"
          />
        </div>

        {/* 未稅總金額 (readonly) */}
        <label>未稅總金額</label>
        <div className="amount-display">{fmt(getField('f2120_28') as number)}</div>

        {/* 含稅別 (Optiongroup: 1=含稅 / 2=免稅) */}
        <label>稅別</label>
        <div className="radio-group">
          <label className="radio-item">
            <input
              type="radio"
              name="taxType"
              value={1}
              checked={(getField('f2120_18') as number) === 1}
              onChange={() => handleTaxTypeChange(1)}
              disabled={!isEditing} data-contract-field="f2120_18"
            />
            含稅(5%)
          </label>
          <label className="radio-item">
            <input
              type="radio"
              name="taxType"
              value={2}
              checked={(getField('f2120_18') as number) !== 1}
              onChange={() => handleTaxTypeChange(2)}
              disabled={!isEditing} data-contract-field="f2120_18"
            />
            免稅
          </label>
        </div>

        {/* 稅額 (readonly) */}
        <label>稅額</label>
        <div className="amount-display">{fmtInt(getField('f2120_19') as number)}</div>

        {/* 含稅總金額 (readonly) */}
        <label data-contract-field="f2120_20">含稅總金額</label>
        <div className="amount-display" style={{ fontWeight: 600 }}>{fmt(getField('f2120_20') as number)}</div>

        {/* 已收總金額 (F2120_21) */}
        <label>已收總金額</label>
        <div>
          <input
            type="number"
            value={getField('f2120_21') as number ?? ''}
            onChange={e => setField('f2120_21', parseFloat(e.target.value) || 0)}
            disabled={!isEditing}
            style={{ width: 140, textAlign: 'right' }}
            step="0.01" data-contract-field="f2120_21"
          />
        </div>

        {/* 待收金額 (display only) */}
        <label data-contract-field="f2120_20">待收金額</label>
        <div className="amount-display" style={{ color: pendingAmount > 0 ? '#cf1322' : '#389e0d' }}>
          {fmt(pendingAmount)}
        </div>

        {/* 發票號碼 (F2120_14) */}
        <label>發票號碼</label>
        <div>
          <input
            type="text"
            value={String(getField('f2120_14') || '')}
            onChange={e => setField('f2120_14', e.target.value)}
            disabled={!isEditing}
            maxLength={12}
            style={{ width: 140 }} data-contract-field="f2120_14"
          />
        </div>
      </div>

      {/* 備註 (F2120_30 textarea) */}
      <div className="memo-group" style={{ marginTop: 16 }}>
        <label>備註</label>
        <textarea
          value={String(getField('f2120_30') || '')}
          onChange={e => setField('f2120_30', e.target.value)}
          disabled={!isEditing}
          rows={8}
          style={{ width: '100%' }} data-contract-field="f2120_30"
        />
      </div>
    </div>
  );
});

Tab3AmountMemo.displayName = 'Tab3AmountMemo';
export default Tab3AmountMemo;
